import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, ArrowLeft, PencilSimple, Trash, ArrowsLeftRight, User, Users, ListChecks, Kanban, ChartBar, DownloadSimple, FilePdf, CheckCircle, ArrowCounterClockwise, ShareNetwork } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canManagePlanner } from '@/auth/AuthContext';
import { DataTable } from '@/components/ui/DataTable';
import { plannerApi } from './plannerApi';
import { fmtDate, fmtDateFull, PRIORITY_BADGE, TASK_STATUS_DOT, PROJECT_STATUS_BADGE, projectBadgeKey, PRIORITY_BAR, PRIORITIES, TASK_STATUSES, ganttOrder, makeNameShortener } from './plannerConstants';
import GanttChart from './components/GanttChart';
import { printGanttChart, buildTaskListSection } from './ganttPrint';
import KanbanBoard from './components/KanbanBoard';
import TaskPanel from './components/TaskPanel';
import ProjectFormDialog from './components/ProjectFormDialog';
import ShareGanttDialog from './components/ShareGanttDialog';
import ArchivedContextSection from './components/ArchivedContextSection';
import LinkedDocsPanel from '@/pages/docs/LinkedDocsPanel';
import { dmsApi } from '@/pages/docs/dmsApi';
import TraceabilityPanel from '@/pages/trace/TraceabilityPanel';
import { traceConfig } from '@/pages/trace/traceApi';
import ProjectFlowsPanel from '@/pages/engineering/itemflow/ProjectFlowsPanel';
import { Files, Graph, FlowArrow } from '@phosphor-icons/react';

const ALL = '__all__';

export default function ProjectBoard() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskParam = searchParams.get('task') || '';
  const fromParam = searchParams.get('from') || '';
  const { t } = useT();
  const navigate = useNavigate();
  const ctx = useOutletContext();
  const { user } = useAuth();
  const myEid = user?.employee_id;
  const [project, setProject] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // 'loading' | 'ok' | 'notfound' | 'error'
  const [loadError, setLoadError] = useState('');
  const [people, setPeople] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [fStatus, setFStatus] = useState(ALL);
  const [fPriority, setFPriority] = useState(ALL);
  const [fMine, setFMine] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [docsEnabled, setDocsEnabled] = useState(false);
  const [traceOn, setTraceOn] = useState(false);
  const [view, setView] = useState('list');

  useEffect(() => {
    traceConfig().then((c) => setTraceOn(!!(c?.enabled && c?.modules?.projects))).catch(() => {});
  }, []);

  useEffect(() => {
    dmsApi.linked('project', projectId)
      .then((r) => setDocsEnabled(!!r?.enabled))
      .catch(() => setDocsEnabled(false));
  }, [projectId]);

  const fetchProject = useCallback((silent) => {
    if (!silent) {
      setLoadState('loading');
      setLoadError('');
    }
    plannerApi.project(projectId)
      .then((p) => { setProject(p); setLoadState('ok'); })
      .catch((e) => {
        if (silent) return;
        setProject(null);
        const status = e?.response?.status;
        if (status === 404) {
          setLoadState('notfound');
        } else {
          setLoadState('error');
          setLoadError(e?.response?.data?.detail || t('planner.project.loadFailed'));
        }
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[ProjectBoard] load failed', { projectId, status, error: e });
        }
      });
  }, [projectId, t]);
  const load = useCallback(() => fetchProject(false), [fetchProject]);
  // タブ表示（カンバン等）を保ったまま裏で再取得する — 全画面ローディングにしない
  const refresh = useCallback(() => fetchProject(true), [fetchProject]);
  useEffect(() => {
    load();
    plannerApi.people().then(setPeople).catch(() => {});
    plannerApi.departments().then(setDepartments).catch(() => {});
  }, [load]);

  // ホームやマイタスクから ?task=<id> で来たら、そのタスクを開く（早期 return より前）
  useEffect(() => {
    if (!taskParam) return;
    const hit = (project?.tasks || []).find((tk) => tk.id === taskParam);
    if (hit) { setActiveTask(hit); setPanelOpen(true); }
  }, [taskParam, project]);

  if (loadState === 'loading') {
    return (
      <div className="py-24 text-center text-sm text-zinc-500" data-testid="planner-project-loading">
        {t('planner.project.loading')}
      </div>
    );
  }
  if (loadState === 'notfound') {
    return (
      <div className="max-w-lg mx-auto py-16 px-6 text-center" data-testid="planner-project-notfound">
        <div className="text-lg font-semibold text-zinc-800 mb-2">{t('planner.project.notFound')}</div>
        <div className="text-sm text-zinc-500 mb-6">{t('planner.project.notFoundHint', { id: projectId })}</div>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={() => navigate('/planner')} data-testid="planner-project-back">
            {t('planner.project.backToList')}
          </Button>
        </div>
      </div>
    );
  }
  if (loadState === 'error') {
    return (
      <div className="max-w-lg mx-auto py-16 px-6 text-center" data-testid="planner-project-error">
        <div className="text-lg font-semibold text-zinc-800 mb-2">{t('planner.project.openFailed')}</div>
        <div className="text-sm text-zinc-500 mb-6">{loadError}</div>
        <div className="flex gap-2 justify-center">
          <Button variant="outline" onClick={() => navigate('/planner')} data-testid="planner-project-back-err">
            {t('planner.project.backToList')}
          </Button>
          <Button onClick={load} data-testid="planner-project-retry">{t('planner.retry')}</Button>
        </div>
      </div>
    );
  }
  if (!project) return null;

  const tasks = project.tasks || [];
  // 一覧はガントと同じ順序（優先度 → 終了日）で表示
  const listTasks = tasks.filter((tk) =>
    (fStatus === ALL || tk.status === fStatus) &&
    (fPriority === ALL || tk.priority === fPriority) &&
    (!fMine || (tk.member_employee_ids || []).includes(myEid)))
    .sort(ganttOrder((tk) => tk.due_date || tk.start_date, (tk) => tk.start_date || tk.due_date));

  const openNew = () => { setActiveTask(null); setPanelOpen(true); };
  // B5 — どのタブから開いても URL に ?task=<id> を残す（ブラウザの戻るで閉じられる）。
  const openTask = (tk) => {
    setActiveTask(tk);
    setPanelOpen(true);
    if (taskParam !== tk.id) {
      const next = new URLSearchParams(searchParams);
      next.set('task', tk.id);
      setSearchParams(next, { replace: true });
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    if (taskParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  };

  // 戻る先は「来た場所」。直接開いたときだけ部署へ。
  const goBack = () => navigate(fromParam || `/planner/dept/${project.department_id}`);

  const moveTask = async (id, status) => {
    try { await plannerApi.updateTask(id, { status }); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  const toggleArchive = async () => {
    if (!window.confirm(t(project.archived ? 'planner.project.confirmRestore' : 'planner.project.confirmArchive'))) return;
    try { await plannerApi.archiveProject(project.id, { archived: !project.archived }); toast.success(t('planner.saved')); ctx?.reloadSidebar?.(); navigate(`/planner/dept/${project.department_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  const removeProject = async () => {
    if (!window.confirm(t('planner.project.confirmDelete', { name: project.name }))) return;
    try { await plannerApi.deleteProject(project.id); toast.success(t('planner.project.deleted')); ctx?.reloadSidebar?.(); navigate(`/planner/dept/${project.department_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  const doMove = async () => {
    if (!moveTarget || moveTarget === project.department_id) { setMoveOpen(false); return; }
    try { await plannerApi.updateProject(project.id, { department_id: moveTarget }); toast.success(t('planner.project.moved')); setMoveOpen(false); ctx?.reloadSidebar?.(); navigate(`/planner/dept/${moveTarget}`); }
    catch (e) { toast.error(e?.response?.data?.detail || t('planner.error')); }
  };

  // ガント・印刷とも優先度→終了日の順（画面のガント/一覧と同じルール）
  const ganttItems = tasks.map((tk) => ({
    id: tk.id, label: tk.title, sub: (tk.member_names || []).join('、'),
    start: tk.start_date || tk.due_date, end: tk.due_date || tk.start_date,
    color: PRIORITY_BAR[tk.priority] || '#4F46E5', priority: tk.priority,
  })).sort(ganttOrder((it) => it.end, (it) => it.start));

  const esc = (s = '') => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/planner/projects/${projectId}/export.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `planner_${project.name}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(t('planner.error')); }
  };

  const exportPdf = async () => {
    const oneOrRange = (s, e) => {
      if (!s && !e) return '';
      if (!s) return fmtDateFull(e);
      if (!e) return fmtDateFull(s);
      const sd = String(s).slice(0, 10);
      const ed = String(e).slice(0, 10);
      return sd === ed ? fmtDateFull(s) : `${fmtDateFull(s)}〜${fmtDateFull(e)}`;
    };
    const printTasks = [...tasks].sort(ganttOrder((tk) => tk.due_date || tk.start_date, (tk) => tk.start_date || tk.due_date));
    // 備考が1件も無いときは備考列を出さない（空列でレイアウトが崩れる・他列が潰れるため）
    const hasNotes = printTasks.some((tk) => (tk.notes || '').trim());
    // メンバーは先頭名のみ表示。同じ先頭名が複数人いる場合だけフルネームで区別する。
    const displayName = makeNameShortener(printTasks.flatMap((tk) => tk.member_names || []));
    const rowsHtml = printTasks.map((tk) => `<tr><td>${esc(tk.title)}</td><td>${esc(t(`planner.taskStatus.${tk.status}`))}</td><td>${esc(t(`planner.priority.${tk.priority}`))}</td><td class="td-mem">${esc((tk.member_names || []).map(displayName).join('、'))}</td><td class="td-date">${esc(oneOrRange(tk.start_date, tk.due_date))}</td>${hasNotes ? `<td class="td-notes">${esc(tk.notes || '')}</td>` : ''}</tr>`).join('');
    const projRange = oneOrRange(project.start_date, project.end_date);
    const listSection = buildTaskListSection({
      heading: t('planner.view.list'),
      headerLabels: hasNotes
        ? [t('planner.task.column'), t('planner.task.status'), t('planner.task.priority'), t('planner.task.members'), t('planner.task.range'), t('planner.task.remark')]
        : [t('planner.task.column'), t('planner.task.status'), t('planner.task.priority'), t('planner.task.members'), t('planner.task.range')],
      rowsHtml,
      withNotes: hasNotes,
    });
    const w = await printGanttChart({
      title: project.name,
      meta: `${project.owner_name || ''} ・ ${projRange}`,
      ganttHeading: t('planner.view.gantt'),
      items: ganttItems,
      emptyText: t('planner.gantt.emptyTasks'),
      itemLabel: t('planner.gantt.item'),
      listSection,
      shareKind: 'project',
      shareId: project.id,
    });
    if (!w) toast.error(t('planner.popupBlocked'));
  };

  const assigneePeople = people; // assign from full directory

  return (
    <div className="space-y-5" data-testid="planner-project-board">
      <button onClick={goBack} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-indigo-600" data-testid="planner-back-btn">
        <ArrowLeft size={15} /> {fromParam ? t('planner.project.backGeneric') : t('planner.project.back')}
      </button>

      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold tracking-tight text-zinc-900 truncate" data-no-translate>{project.name}</h1>
              <Badge variant="outline" className={`${PRIORITY_BADGE[project.priority]} text-[10px] px-1.5 py-0`}>{t(`planner.priority.${project.priority}`)}</Badge>
              <Badge variant="outline" data-testid="planner-project-status-badge"
                className={`${PROJECT_STATUS_BADGE[projectBadgeKey(project)]} text-[10px] px-1.5 py-0`}>{t(`planner.projectStatus.${projectBadgeKey(project)}`)}</Badge>
            </div>
            {project.description && <p className="text-sm text-zinc-500 mb-2">{project.description}</p>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
              {project.owner_name && <span className="inline-flex items-center gap-1"><User size={13} weight="duotone" /> {t('planner.project.owner')}: {project.owner_name}</span>}
              {project.member_names?.length > 0 && <span className="inline-flex items-center gap-1"><Users size={13} weight="duotone" /> {project.member_names.join('、')}</span>}
              <span>{fmtDateFull(project.start_date)} 〜 {fmtDateFull(project.end_date)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {canManagePlanner(user) && (<>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="planner-edit-project-btn"><PencilSimple size={15} className="mr-1" /> {t('planner.edit')}</Button>
            <Button variant="outline" size="sm" onClick={() => { setMoveTarget(project.department_id); setMoveOpen(true); }} data-testid="planner-move-project-btn" className="text-zinc-600"><ArrowsLeftRight size={15} className="mr-1" /> {t('planner.project.move')}</Button>
            <Button variant="outline" size="sm" onClick={toggleArchive} data-testid="planner-archive-project-btn" className={project.archived ? 'text-zinc-600' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'}>
              {project.archived
                ? <><ArrowCounterClockwise size={15} className="mr-1" /> {t('planner.project.restore')}</>
                : <><CheckCircle size={15} className="mr-1" /> {t('planner.project.archive')}</>}
            </Button>
            </>)}
            {canManagePlanner(user) && (
              <Button variant="outline" size="sm" onClick={removeProject} data-testid="planner-delete-project-btn" className="text-red-600 hover:bg-red-50 border-red-200"><Trash size={15} className="mr-1" /> {t('planner.project.delete')}</Button>
            )}
          </div>
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList data-testid="planner-view-tabs">
            <TabsTrigger value="list" data-testid="planner-tab-list"><ListChecks size={15} className="mr-1.5" /> {t('planner.view.list')}</TabsTrigger>
            <TabsTrigger value="kanban" data-testid="planner-tab-kanban"><Kanban size={15} className="mr-1.5" /> {t('planner.view.kanban')}</TabsTrigger>
            <TabsTrigger value="gantt" data-testid="planner-tab-gantt"><ChartBar size={15} className="mr-1.5" /> {t('planner.view.gantt')}</TabsTrigger>
            {docsEnabled && <TabsTrigger value="docs" data-testid="planner-tab-docs"><Files size={15} className="mr-1.5" /> {t('planner.tab.docs')}</TabsTrigger>}
            {traceOn && <TabsTrigger value="trace" data-testid="planner-tab-trace"><Graph size={15} className="mr-1.5" /> {t('planner.tab.trace')}</TabsTrigger>}
            <TabsTrigger value="itemflow" data-testid="planner-tab-itemflow"><FlowArrow size={15} className="mr-1.5" /> {t('planner.tab.itemflow')}</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn"><DownloadSimple size={15} className="mr-1" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={exportPdf} data-testid="export-pdf-btn"><FilePdf size={15} className="mr-1" /> PDF</Button>
            <Button onClick={openNew} data-testid="planner-new-task-btn" className="bg-teal-700 hover:bg-teal-800"><Plus size={18} weight="bold" className="mr-1" /> {t('planner.task.new')}</Button>
          </div>
        </div>        <TabsContent value="list" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="h-9 w-auto min-w-[130px]" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>{t('planner.filter.allStatus')}</SelectItem>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`planner.taskStatus.${s}`)}</SelectItem>)}</SelectContent></Select>
            <Select value={fPriority} onValueChange={setFPriority}><SelectTrigger className="h-9 w-auto min-w-[130px]" data-testid="filter-priority"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>{t('planner.filter.allPriority')}</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`planner.priority.${p}`)}</SelectItem>)}</SelectContent></Select>
            {myEid && (
              <button onClick={() => setFMine((v) => !v)} data-testid="filter-mine"
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border transition-colors ${fMine ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
                <User size={15} weight={fMine ? 'fill' : 'regular'} /> {t('planner.filter.mine')}
              </button>
            )}
          </div>
          <DataTable
            dataTestId="planner-task-table"
            rows={listTasks}
            rowKey={(tk) => tk.id}
            rowTestId={(tk) => `task-row-${tk.id}`}
            onRowClick={(tk) => openTask(tk)}
            empty={{ icon: <ListChecks size={36} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('planner.task.empty') }}
            columns={[
              { key: 'title', primary: true, header: t('planner.task.title'), width: 'w-2/5 min-w-[220px]', cell: (tk) => <span className="font-medium text-zinc-800" data-no-translate>{tk.title}</span> },
              { key: 'priority', header: t('planner.task.priority'), mobileBadge: true, width: 'w-28', cell: (tk) => <Badge variant="outline" className={`${PRIORITY_BADGE[tk.priority]} text-[11px]`}>{t(`planner.priority.${tk.priority}`)}</Badge> },
              { key: 'status', header: t('planner.task.status'), width: 'w-32', cell: (tk) => <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600"><span className={`w-2 h-2 rounded-full ${TASK_STATUS_DOT[tk.status]}`} />{t(`planner.taskStatus.${tk.status}`)}</span> },
              { key: 'members', header: t('planner.task.members'), width: 'w-40', cell: (tk) => { const txt = (tk.member_names && tk.member_names.length > 0) ? tk.member_names.join('、') : '—'; return <span className="text-zinc-600 text-xs md:block md:max-w-[160px] md:truncate" title={txt}>{txt}</span>; } },
              { key: 'due', header: t('planner.task.due'), align: 'right', width: 'w-28', sortValue: (tk) => tk.due_date, cell: (tk) => <span className="tabular-nums text-zinc-600 text-xs">{fmtDate(tk.due_date)}</span> },
            ]}
          />
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          <KanbanBoard tasks={tasks} onMove={moveTask} onCardClick={openTask} />
        </TabsContent>

        <TabsContent value="gantt" className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} data-testid="planner-gantt-share-btn"
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50">
              <ShareNetwork size={15} className="mr-1" /> {t('planner.share')}
            </Button>
          </div>
          <GanttChart items={ganttItems} onItemClick={(it) => { const tk = tasks.find((x) => x.id === it.id); if (tk) openTask(tk); }} emptyText={t('planner.gantt.emptyTasks')} />
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <LinkedDocsPanel targetType="project" targetId={projectId} />
        </TabsContent>

        <TabsContent value="trace" className="mt-4">
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5">
            <TraceabilityPanel entityType="project" entityId={projectId} />
          </div>
        </TabsContent>

        <TabsContent value="itemflow" className="mt-4">
          <ProjectFlowsPanel projectId={projectId} />
        </TabsContent>
      </Tabs>

      <ArchivedContextSection projectId={project.id} />

      <TaskPanel open={panelOpen} onClose={closePanel} task={activeTask} projectId={projectId} people={assigneePeople} defaultPriority="medium" onSaved={refresh} />
      <ShareGanttDialog open={shareOpen} onClose={() => setShareOpen(false)} kind="project" targetId={project.id} title={project.name} />
      <ProjectFormDialog open={editOpen} onClose={() => setEditOpen(false)} departmentId={project.department_id} project={project} people={people} onSaved={() => { refresh(); ctx?.reloadSidebar?.(); }} />

      <Dialog open={moveOpen} onOpenChange={(o) => !o && setMoveOpen(false)}>
        <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="move-project-dialog">
          <DialogHeader><DialogTitle>{t('planner.project.moveTitle')}</DialogTitle></DialogHeader>
          <div>
            <label className="text-xs font-semibold text-zinc-600">{t('planner.project.moveTarget')}</label>
            <Select value={moveTarget} onValueChange={setMoveTarget}>
              <SelectTrigger className="h-10 mt-1" data-testid="move-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>{t('planner.cancel')}</Button>
            <Button onClick={doMove} data-testid="move-confirm-btn" className="bg-teal-700 hover:bg-teal-800">{t('planner.project.move')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
